import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { ExposeToggleControl } from "../controls/expose_toggle_control"
import { TextNumericControl } from "../controls/text_numeric"
import { categoricalDataSocket, numericDataSocket, numericNumberDataSocket } from "../socket_types"
import { CategoricalTileGrid, NumericTileGrid } from "../tile_grid"
import { NumericConstant } from "../numeric_constant"
import { BaseComponent } from "./base_component"
import { CategoricalLabelRow, getCategoricalLabelsFromInputChain } from "./categorical_label_chain"
import {
  isCategoricalChainSeedingSuppressed,
  isLabelOutputReconnectInProgressForNode,
  mergeLabelInputWireSnapshotsForReconnect,
  PENDING_LABEL_INPUT_WIRE_DATA_KEY,
  reconnectLabelInputsByNormalizedLabel,
  rememberLabelInputWireSnapshots,
  runWithLabelOutputReconnect,
  snapshotLabelInputConnections,
  takePendingLabelInputWireSnapshots,
} from "./categorical_dynamic_io_connections"

const CAT_INPUT_KEY = "in"

function inputKeyForValue(value: number): string {
  return `catVal_${value}`
}

export const CATEGORICAL_TO_NUMERIC_COMPONENT_NAME = "Categorical to numeric dataset"

export function mapValDataKeyForCategoryValue(value: number): string {
  return `mapVal_${value}`
}

function dataKeyForValue(value: number): string {
  return mapValDataKeyForCategoryValue(value)
}

export function normalizeCatMapLabelRows(raw: unknown): CategoricalLabelRow[] {
  return normalizeSavedRows(raw)
}

export function catMapNumericValueString(data: Record<string, unknown> | undefined, categoryValue: number): string {
  if (!data) return "0"
  const raw = data[dataKeyForValue(categoryValue)]
  if (typeof raw === "number" && !Number.isNaN(raw)) {
    return String(raw)
  }
  if (typeof raw === "string") {
    return raw
  }
  return "0"
}

export function persistCatMapValuesByLabelOnNode(node: Node): void {
  const rows = Array.isArray(node.data.catMapLabels) ? normalizeSavedRows(node.data.catMapLabels) : []
  const valuesByLabel: Record<string, number> = {}
  rows.forEach(row => {
    const value = parseNumericFromData(node.data[dataKeyForValue(row.value)])
    if (!Number.isNaN(value)) {
      valuesByLabel[normalizeLabel(row.label)] = value
    }
  })
  node.data.catMapValuesByLabel = valuesByLabel
}

function labelFingerprint(rows: CategoricalLabelRow[]): string {
  return JSON.stringify(rows.map(r => [r.value, r.label]))
}

function extractLabelRows(grid: CategoricalTileGrid): CategoricalLabelRow[] {
  const map = grid.labels ?? new Map<number, string>()
  const rows: CategoricalLabelRow[] = []
  const entries = Array.from(map.entries())
  for (let i = 0; i < entries.length; i++) {
    const value = entries[i][0]
    const label = entries[i][1]
    if (value === 255) continue
    const text = typeof label === "string" ? label.trim() : ""
    rows.push({ value, label: text || `Category ${value}` })
  }
  rows.sort((a, b) => a.value - b.value)
  return rows
}

function normalizeSavedRows(raw: unknown): CategoricalLabelRow[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((r: any) => ({
      value: Number(r?.value),
      label: String(r?.label ?? "").trim() || `Category ${r?.value}`,
    }))
    .filter(r => Number.isFinite(r.value) && r.value !== 255)
    .sort((a, b) => a.value - b.value)
}

function formatMapValueSuffix(data: Record<string, unknown>, valueKeys: number[]): string {
  const parts: string[] = []
  for (const v of valueKeys) {
    const key = dataKeyForValue(v)
    const raw = data[key]
    if (typeof raw === "number" && !Number.isNaN(raw)) {
      parts.push(`[${raw}]`)
    } else if (typeof raw === "string" && raw.trim() !== "") {
      const parsed = Number(raw)
      if (!Number.isNaN(parsed)) parts.push(`[${parsed}]`)
    }
  }
  return parts.length > 0 ? ` ${parts.join(" ")}` : ""
}

function normalizeLabel(label: string): string {
  return label.trim().toLowerCase()
}

function parseNumericFromData(raw: unknown): number {
  if (typeof raw === "number") return raw
  if (typeof raw === "string" && raw.trim() !== "") {
    const parsed = Number(raw)
    return Number.isNaN(parsed) ? NaN : parsed
  }
  return NaN
}

function controlKeyForRow(row: CategoricalLabelRow): string {
  const safeLabel = row.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
  return `_cap${row.value}_${safeLabel || "label"}`
}

function readSavedValuesByLabel(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object") return {}
  const obj = raw as Record<string, unknown>
  const out: Record<string, number> = {}
  Object.keys(obj).forEach(key => {
    const parsed = parseNumericFromData(obj[key])
    if (!Number.isNaN(parsed)) out[key] = parsed
  })
  return out
}

/**
 * Keeps `catMapValuesByLabel` aligned with `mapVal_*` on serialized model data.
 * UI controls update `mapVal_*` immediately; `catMapValuesByLabel` is normally refreshed in `worker`
 * (engine.process). Without this, PATCH payloads can store all zeros in `catMapValuesByLabel` while
 * `mapVal_*` are correct — and on reload the old hydration overwrote `mapVal_*` with those zeros.
 */
export function syncCatMapValuesByLabelInReteModelData(model: { nodes?: Record<string, { name?: string; data?: Record<string, unknown> }> } | null) {
  if (!model?.nodes) return
  for (const id of Object.keys(model.nodes)) {
    const n = model.nodes[id]
    if (!n || n.name !== "Categorical to numeric dataset" || !n.data) continue
    const rows = normalizeSavedRows(n.data.catMapLabels)
    if (rows.length === 0) continue
    const valuesByLabel: Record<string, number> = {}
    for (const row of rows) {
      const dk = dataKeyForValue(row.value)
      const v = parseNumericFromData(n.data[dk])
      if (!Number.isNaN(v)) valuesByLabel[normalizeLabel(row.label)] = v
    }
    n.data.catMapValuesByLabel = valuesByLabel
  }
}

export class CategoricalToNumericDatasetComponent extends BaseComponent {
  private static connectionHooksByNodeId = new Set<number>()

  constructor() {
    super("Categorical to numeric dataset")
    this.category = "Conversions"
    this.contextMenuName = "Categorical to numeric dataset"
  }

  async builder(node: Node) {
    node.addInput(new Input(CAT_INPUT_KEY, "Categorical dataset", categoricalDataSocket))
    node.addOutput(new Output("out", "Numeric dataset", numericDataSocket))

    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }
    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }

    const saved = node.data.catMapLabels
    const savedRows = Array.isArray(saved) ? normalizeSavedRows(saved) : []
    if (savedRows.length > 0) {
      // Saved labels already describe every dynamic input we need to
      // recreate, so add them directly and skip the upstream chain walk.
      // `applyLabelInputRows` would still short-circuit on a fingerprint
      // match — but only AFTER paying for the chain traversal and a full
      // clear/rebuild dance, neither of which is needed on first build.
      this.hydrateLabelInputsFromSaved(node, savedRows)
    } else {
      // Brand-new node: no saved label set, so derive one from the upstream
      // categorical chain (if any input is already wired). `allowShrink:
      // false` is harmless here because `existing` is empty.
      this.seedInputsFromConnections(node, { allowShrink: false })
    }

    if (this.editor && !CategoricalToNumericDatasetComponent.connectionHooksByNodeId.has(node.id)) {
      CategoricalToNumericDatasetComponent.connectionHooksByNodeId.add(node.id)
      const refreshOnConnectionChange = () => {
        // Skip during `editor.fromJSON` — the saved label set was just
        // restored into `node.data.catMapLabels`, and the chain walk would
        // fire once PER restored connection PER cat-to-num node (O(N*M*D)
        // for N nodes / M connections / chain depth D). Worker / explicit
        // user edits still run this path via `connectioncreated` after the
        // load settles.
        if (isCategoricalChainSeedingSuppressed()) return
        this.seedInputsFromConnections(node, { allowShrink: true })
        this.refreshCatValInputControls(node)
      }
      this.editor.on("connectioncreated", refreshOnConnectionChange)
      this.editor.on("connectionremoved", refreshOnConnectionChange)
      this.editor.on("noderemoved", (removedNode: any) => {
        if (removedNode?.id === node.id) {
          CategoricalToNumericDatasetComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  /**
   * Recreates the per-label `catVal_*` inputs directly from the saved
   * `catMapLabels` rows — for use during initial `builder()` only. Assumes
   * no dynamic inputs exist on the node yet (true on first build) and no
   * upstream wires need to be remembered/restored (the JSON connection
   * replay handles that). The full bookkeeping path is
   * `applyLabelInputRows`; this is the cheap subset that skips:
   *   - chain traversal
   *   - clear+rebuild of dynamic inputs
   *   - wire snapshot / reconnect machinery
   *   - synchronous `node.update()` (deferred render storm guard in
   *     `model_view` would batch it anyway, but skipping is one less
   *     enqueued render).
   */
  private hydrateLabelInputsFromSaved(node: Node, rows: CategoricalLabelRow[]) {
    const fp = labelFingerprint(rows)
    const revision = Number(node.data._catLabelRevision ?? 0) + 1
    node.data._catLabelRevision = revision
    node.data._catLabelFingerprint = fp
    node.data.catMapLabels = rows

    for (const row of rows) {
      const dk = dataKeyForValue(row.value)
      // `mapVal_*` should already be on `node.data` from JSON. Only seed
      // when truly missing so we never clobber the user's saved values.
      if (!(dk in node.data)) {
        node.data[dk] = 0
      }

      const inp = new Input(inputKeyForValue(row.value), row.label, numericNumberDataSocket)
      inp.addControl(
        new TextNumericControl(
          this.editor,
          `${controlKeyForRow(row)}_r${revision}`,
          dk,
          row.label,
          "Numeric input",
          "",
          "",
          true,
          () => inp.connections.length > 0
        )
      )
      node.addInput(inp)
    }

    this.persistValuesByLabel(node)
  }

  /** Remove dynamic per-label inputs; keep categorical `in` and outputs. */
  private clearDynamicLabelInputs(node: Node, opts?: { rememberConnections?: boolean }) {
    const remember = opts?.rememberConnections !== false
    if (remember && this.editor) {
      rememberLabelInputWireSnapshots(
        node,
        snapshotLabelInputConnections(node, k => k.startsWith("catVal_"))
      )
    }
    const dynamic = Array.from(node.inputs.values()).filter(i => String(i.key).startsWith("catVal_"))
    for (const input of dynamic) {
      const conns = [...input.connections]
      for (const c of conns) {
        this.editor?.removeConnection(c)
      }
      node.removeInput(input)
    }
    delete node.data.catMapLabels
    delete node.data._catLabelFingerprint
    delete node.data._catLabelRevision
  }

  /**
   * Sync Rete inputs to one row per categorical label. Skips structural work when
   * `rows` matches the stored fingerprint (same categorical label set).
   */
  private applyLabelInputRows(node: Node, rows: CategoricalLabelRow[], opts?: { force?: boolean }) {
    const fp = labelFingerprint(rows)
    const rawPending = node.data[PENDING_LABEL_INPUT_WIRE_DATA_KEY]
    const hasPendingInputRewires = Array.isArray(rawPending) && rawPending.length > 0
    if (!opts?.force && node.data._catLabelFingerprint === fp && !hasPendingInputRewires) {
      return
    }
    const nextRevision = Number(node.data._catLabelRevision ?? 0) + 1
    node.data._catLabelRevision = nextRevision

    const previousRows = Array.isArray(node.data.catMapLabels)
      ? normalizeSavedRows(node.data.catMapLabels)
      : []
    const savedByLabel = readSavedValuesByLabel(node.data.catMapValuesByLabel)
    const previousByNormalizedLabel = new Map<string, number>()
    previousRows.forEach(prevRow => {
      previousByNormalizedLabel.set(
        normalizeLabel(prevRow.label),
        parseNumericFromData((node.data as Record<string, unknown>)[dataKeyForValue(prevRow.value)])
      )
    })
    const nextDataKeys = new Set(rows.map(row => dataKeyForValue(row.value)))

    const pending = takePendingLabelInputWireSnapshots(node)
    const live =
      this.editor != null ? snapshotLabelInputConnections(node, k => k.startsWith("catVal_")) : []
    const wireSnaps = mergeLabelInputWireSnapshotsForReconnect(live, pending)

    this.clearDynamicLabelInputs(node, { rememberConnections: false })

    // Drop stale per-label numeric values from previous label sets.
    Object.keys(node.data)
      .filter(key => key.startsWith("mapVal_") && !nextDataKeys.has(key))
      .forEach(key => {
        delete (node.data as Record<string, unknown>)[key]
      })

    node.data.catMapLabels = rows
    node.data._catLabelFingerprint = fp

    for (const row of rows) {
      const dk = dataKeyForValue(row.value)
      const labelKey = normalizeLabel(row.label)
      const remappedValue = previousByNormalizedLabel.get(labelKey)
      const savedLabelValue = savedByLabel[labelKey]
      // Prefer remapped in-memory values, then mapVal_* already on the node (e.g. from JSON after reload).
      // Do not let stale catMapValuesByLabel (often all zeros — it only updates on engine.process) overwrite real mapVal_*.
      const fromDisk = parseNumericFromData((node.data as Record<string, unknown>)[dk])
      if (typeof remappedValue === "number" && !Number.isNaN(remappedValue)) {
        node.data[dk] = remappedValue
      } else if (typeof fromDisk === "number" && !Number.isNaN(fromDisk)) {
        node.data[dk] = fromDisk
      } else if (typeof savedLabelValue === "number" && !Number.isNaN(savedLabelValue)) {
        node.data[dk] = savedLabelValue
      } else if (!(dk in node.data)) {
        node.data[dk] = 0
      }

      const inp = new Input(inputKeyForValue(row.value), row.label, numericNumberDataSocket)
      inp.addControl(
        new TextNumericControl(
          this.editor,
          `${controlKeyForRow(row)}_r${nextRevision}`,
          dk,
          row.label,
          "Numeric input",
          "",
          "",
          true,
          () => inp.connections.length > 0
        )
      )
      node.addInput(inp)
    }
    this.persistValuesByLabel(node)

    if (this.editor) {
      runWithLabelOutputReconnect(node, () =>
        reconnectLabelInputsByNormalizedLabel(this.editor!, node, wireSnaps, k => k.startsWith("catVal_"))
      )
    }

    this.refreshCatValInputControls(node)
    node.update()
  }

  private refreshCatValInputControls(node: Node) {
    node.inputs.forEach(input => {
      if (!String(input.key).startsWith("catVal_")) return
      const control = input.control as { update?: () => Promise<void> } | null
      if (control && typeof control.update === "function") {
        void control.update()
      }
    })
  }

  private persistValuesByLabel(node: Node) {
    const rows = Array.isArray(node.data.catMapLabels) ? normalizeSavedRows(node.data.catMapLabels) : []
    const valuesByLabel: Record<string, number> = {}
    rows.forEach(row => {
      const value = parseNumericFromData(node.data[dataKeyForValue(row.value)])
      if (!Number.isNaN(value)) {
        valuesByLabel[normalizeLabel(row.label)] = value
      }
    })
    node.data.catMapValuesByLabel = valuesByLabel
  }

  private seedInputsFromConnections(node: Node, opts?: { allowShrink?: boolean }) {
    if (!this.editor) return
    if (isLabelOutputReconnectInProgressForNode(node.id)) return
    const existing = Array.isArray(node.data.catMapLabels) ? normalizeSavedRows(node.data.catMapLabels) : []
    const rows = getCategoricalLabelsFromInputChain(this.editor, node, CAT_INPUT_KEY)
    if (rows.length === 0) {
      if (existing.length > 0) return
      return
    }
    const allowShrink = opts?.allowShrink !== false
    if (!allowShrink && existing.length > rows.length) {
      return
    }
    this.applyLabelInputRows(node, rows)
  }

  worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) return

    const input = inputs[CAT_INPUT_KEY]?.[0]

    if (!(input instanceof CategoricalTileGrid)) {
      editorNode.meta.errorMessage = "No categorical input"
      this.clearDynamicLabelInputs(editorNode)
      editorNode.update()
      return
    }

    const rows = extractLabelRows(input)
    this.applyLabelInputRows(editorNode, rows)
    this.persistValuesByLabel(editorNode)

    delete editorNode.meta.errorMessage
    const output = new NumericTileGrid(input.zoom, input.x, input.y, input.width, input.height, NaN)

    const values = rows.map(r => r.value)
    const suffix = formatMapValueSuffix(editorNode.data as Record<string, unknown>, values)

    const rowByValue = new Map<number, CategoricalLabelRow>()
    const rowByLabel = new Map<string, CategoricalLabelRow>()
    rows.forEach(row => {
      rowByValue.set(row.value, row)
      rowByLabel.set(normalizeLabel(row.label), row)
    })

    input.iterate((x, y, catValue) => {
      if (catValue === 255) {
        output.set(x, y, NaN)
        return
      }

      const labelForCell = input.labels?.get(catValue) ?? `Category ${catValue}`
      const row = rowByLabel.get(normalizeLabel(labelForCell)) ?? rowByValue.get(catValue)
      if (!row) {
        output.set(x, y, NaN)
        return
      }

      const socketValue = inputs[inputKeyForValue(row.value)]?.[0]
      if (socketValue instanceof NumericTileGrid) {
        output.set(x, y, socketValue.get(x, y, input.zoom))
        return
      }
      if (socketValue instanceof NumericConstant) {
        output.set(x, y, socketValue.value)
        return
      }
      if (typeof socketValue === "number") {
        output.set(x, y, socketValue)
        return
      }

      output.set(x, y, parseNumericFromData(editorNode.data[dataKeyForValue(row.value)]))
    })

    output.name = ((editorNode.data.name as string) || "Categorical to numeric") + suffix
    outputs["out"] = output

    editorNode.update()
  }
}
